<script setup>
import {onMounted, onUnmounted, nextTick, inject, ref} from 'vue';
import "jquery-ui/ui/version";
import "jquery-ui/ui/widget";
import "jquery-ui/ui/unique-id"
import "jquery-ui/ui/position";

import "jquery-ui/ui/widgets/menu";
import "jquery-ui/ui/widgets/autocomplete";
import {renderWithPoi} from '../utility/addressHelper';

const emailModified = inject('emailModified');
const phoneModified = inject('phoneModified');

const model = defineModel({
  type: Object,
  default: () => ({})
});

let addressInput = ref(null);
const isManualAddress = ref(false);

const toggleManualAddress = () => {
  isManualAddress.value = !isManualAddress.value;
  if (!isManualAddress.value) {
    // Optionally clear fields when switching back to search
    model.value.address = '';
    model.value.city = '';
    model.value.state = '';
    model.value.country = '';
    model.value.postalCode = '';
    
    // Re-initialize autocomplete after DOM updates
    nextTick(() => {
      initAddressAutocomplete();
    });
  }
};

const parseAddressComponents = (addressComponents) => {
  const parsed = {
    streetAddress: '',
    city: '',
    state: '',
    country: '',
    postalCode: ''
  };

  if (!addressComponents || !Array.isArray(addressComponents)) {
    return parsed;
  }

  let streetNumber;
  let routeName;
  addressComponents.forEach((component) => {
    const types = component.types || [];
    if (types.includes('street_number')) {
      streetNumber = component.long_name;
    } else if (types.includes('route')) {
      routeName = component.long_name;
    } else if (types.includes('locality')) {
      parsed.city = component.long_name;
    } else if (!parsed.city && types.includes('postal_town')) {
      parsed.city = component.long_name;
    } else if (!parsed.city && types.includes('sublocality_level_1')) {
      parsed.city = component.long_name;
    }
    if (types.includes('administrative_area_level_1')) {
      parsed.state = component.short_name;
    }
    if (types.includes('country')) {
      parsed.country = component.long_name;
    }
    if (types.includes('postal_code')) {
      parsed.postalCode = component.long_name;
    }
  });

  if (streetNumber && routeName) {
    parsed.streetAddress = `${streetNumber} ${routeName}`;
  }

  return parsed;
};

const initAddressAutocomplete = () => {
  if (!addressInput.value) return;
  try {
    if ($(addressInput.value).data('ui-autocomplete')) {
      $(addressInput.value).autocomplete('destroy');
    }
    $(addressInput.value).autocomplete({
      source: (request, response) => {
        fetch(`/api/dispatch-ride/autocomplete?query=${encodeURIComponent(request.term)}`)
            .then((res) => res.ok ? res.json() : Promise.reject(res.status))
            .then((data) => {
              const suggestions = (data?.results || []).map((result) => ({
                label: renderWithPoi(result),
                value: result.name,
                placeId: result.id,
                result,
              }));
              response(suggestions);
            })
            .catch(() => response([]));
      },
      minLength: 3,
    }).on('autocompleteselect', (event, ui) => {
      const selectedItem = ui.item;

      // Clear the search box after selection
      setTimeout(() => {
        if (addressInput.value) {
          addressInput.value.value = '';
        }
      }, 0);

      if (selectedItem?.placeId) {
        model.value.placeId = selectedItem.placeId;
        fetch(`/api/dispatch-ride/geocode-by-place-id?placeId=${encodeURIComponent(selectedItem.placeId)}`)
            .then((res) => res.ok ? res.json() : Promise.reject(res.status))
            .then((data) => {
              if (data?.results && data.results.length > 0) {
                const addressComponents = data.results[0].address_components;
                const parsed = parseAddressComponents(addressComponents);
                if (parsed.streetAddress) model.value.address = parsed.streetAddress;
                if (parsed.city) model.value.city = parsed.city;
                if (parsed.state) model.value.state = parsed.state;
                if (parsed.country) model.value.country = parsed.country;
                if (parsed.postalCode) model.value.postalCode = parsed.postalCode;
              }
            })
            .catch((err) => {
              console.error('Failed to fetch address details:', err);
            });
      }
    }).autocomplete('instance')._renderItem = (ul, item) => {
      return $('<li>').append(item.label).appendTo(ul);
    };
  } catch (e) {
    console.error('Failed to init address autocomplete', e);
  }
};

onMounted(async () => {
  await nextTick();
  initAddressAutocomplete();
});

onUnmounted(() => {
  try {
    if (addressInput.value) {
      $(addressInput.value).autocomplete('destroy');
    }
  } catch (e) {
  }
});
</script>

<template>
  <div class="signup-step signup-step-personal">
    <div class="row g-3">
      <div class="col-12">
        <h5 class="text-primary mb-3">Account Information</h5>
      </div>

      <div class="col-md-6">
        <label class="form-label f-w-600" for="signup-email-input">Email <span class="text-danger">*</span></label>
        <input id="signup-email-input" class="form-control" type="email" v-model="model.email"
               placeholder="you@example.com" required>
        <div v-if="emailModified" class="text-warning mt-1" style="font-size: 0.85rem;">
          <i class="fa-solid fa-circle-exclamation me-1"></i> Email changed. Re-verification required.
        </div>
        <div id="signup-email-error" class="invalid-feedback d-none">Please enter a valid email address.</div>
      </div>

      <div class="col-md-6"></div>

      <div class="col-md-6">
        <label class="form-label f-w-600" for="signup-password-input">Password <span
            class="text-danger">*</span></label>
        <input id="signup-password-input" class="form-control" type="password" v-model="model.password"
               placeholder="Minimum 8 characters" required>
        <small class="text-muted">Password must be at least 8 characters.</small>
        <div id="signup-password-error" class="invalid-feedback d-none">Password must be at least 8 characters.</div>
      </div>

      <div class="col-md-6">
        <label class="form-label f-w-600" for="signup-confirm-password-input">Confirm Password <span
            class="text-danger">*</span></label>
        <input id="signup-confirm-password-input" class="form-control" type="password" v-model="model.confirmPassword"
               placeholder="Re-enter your password" required>
        <div id="signup-confirm-password-error" class="invalid-feedback d-none">Passwords do not match.</div>
      </div>

      <div class="col-12 mt-4">
        <h5 class="text-primary mb-3">Personal Information</h5>
      </div>

      <div class="col-md-6">
        <label class="form-label f-w-600" for="signup-first-name-input">First Name <span
            class="text-danger">*</span></label>
        <input id="signup-first-name-input" class="form-control" type="text" v-model="model.firstName"
               placeholder="e.g., Jane" required>
        <div id="signup-first-name-error" class="invalid-feedback d-none">Please enter your first name.</div>
      </div>

      <div class="col-md-6">
        <label class="form-label f-w-600" for="signup-last-name-input">Last Name <span
            class="text-danger">*</span></label>
        <input id="signup-last-name-input" class="form-control" type="text" v-model="model.lastName"
               placeholder="e.g., Doe" required>
        <div id="signup-last-name-error" class="invalid-feedback d-none">Please enter your last name.</div>
      </div>

      <div class="col-md-6">
        <label class="form-label f-w-600" for="signup-dob-input">Date of Birth <span
            class="text-danger">*</span></label>
        <input id="signup-dob-input" class="form-control" type="date" v-model="model.dateOfBirth" required>
        <small class="text-muted">You must be at least 25 years old to drive.</small>
        <div id="signup-dob-error" class="invalid-feedback d-none">Please provide a valid date of birth. You must be at
          least 25 years old.
        </div>
      </div>

      <div class="col-md-6">
        <label class="form-label f-w-600" for="signup-phone-number-input">Phone Number <span
            class="text-danger">*</span></label>
        <div class="input-group">
          <select class="form-select" v-model="model.countryCode" style="max-width: 120px;">
            <option value="+1">+1 (US)</option>
            <option value="+1">+1 (CA)</option>
<!--            <option value="+44">+44 (UK)</option>-->
<!--            <option value="+234">+234 (NG)</option>-->
<!--            <option value="+91">+91 (IN)</option>-->
<!--            <option value="+61">+61 (AU)</option>-->
          </select>
          <input id="signup-phone-number-input" class="form-control" type="tel" v-model="model.phoneNumber"
                 placeholder="5551234567" required>
        </div>
        <div v-if="phoneModified" class="text-warning mt-1" style="font-size: 0.85rem;">
          <i class="fa-solid fa-circle-exclamation me-1"></i> Phone number changed. Re-verification required.
        </div>
        <small class="text-muted">Select country code and enter the rest of your phone number.</small>
        <div id="signup-phone-number-error" class="invalid-feedback d-none">Please enter a valid phone number.</div>
      </div>

      <div class="col-md-6">
        <label class="form-label f-w-600" for="signup-referral-code-input">Referral Code <span
            class="text-muted small">(optional)</span></label>
        <input id="signup-referral-code-input" class="form-control" type="text" v-model="model.referralCode"
               placeholder="e.g., ABC12345">
        <div id="signup-referral-code-error" class="invalid-feedback d-none">This referral code isn't valid.</div>
      </div>

      <div class="col-12 mt-4">
        <h5 class="text-primary mb-1">Address</h5>
        <p class="text-muted small mb-3">
          Search for your address using the search bar below. If you can't find it, click "Enter address manually" to type it in yourself.
        </p>
      </div>

      <div class="col-12" v-if="!isManualAddress">
        <label class="form-label f-w-600" for="signup-address-search">Address Search <span class="text-danger">*</span></label>
        <input id="signup-address-search" class="form-control" type="text"
               placeholder="Search for your address..." ref="addressInput">
        <div class="mt-2">
          <a href="javascript:void(0)" @click="toggleManualAddress" class="text-primary small f-w-600">
            Can't find your address? Enter address manually
          </a>
        </div>
      </div>

      <div class="col-12" v-else>
        <div class="alert alert-info py-2 mb-3">
          <i class="fa-solid fa-circle-info me-2"></i>
          <span class="small">You are now in manual entry mode. Please fill out all address fields below.</span>
          <a href="javascript:void(0)" @click="toggleManualAddress" class="ms-2 text-primary small f-w-600">
            Switch back to search
          </a>
        </div>
      </div>

      <div class="col-12">
        <label class="form-label f-w-600" for="signup-address-input">Street Address <span
            class="text-danger">*</span></label>
        <input id="signup-address-input" class="form-control" type="text" v-model="model.address"
               placeholder="123 Main Street" required :readonly="!isManualAddress">
        <div id="signup-address-error" class="invalid-feedback d-none">Please enter your street address.</div>
      </div>

      <div class="col-md-6">
        <label class="form-label f-w-600" for="signup-city-input">City <span class="text-danger">*</span></label>
        <input id="signup-city-input" class="form-control" type="text" v-model="model.city"
               placeholder="e.g., San Francisco" required :readonly="!isManualAddress">
        <div id="signup-city-error" class="invalid-feedback d-none">Please enter your city.</div>
      </div>

      <div class="col-md-6">
        <label class="form-label f-w-600" for="signup-state-input">State/Province <span
            class="text-danger">*</span></label>
        <input id="signup-state-input" class="form-control" type="text" v-model="model.state"
               placeholder="e.g., CA" required :readonly="!isManualAddress">
        <div id="signup-state-error" class="invalid-feedback d-none">Please enter your state.</div>
      </div>

      <div class="col-md-6">
        <label class="form-label f-w-600" for="signup-country-input">Country <span class="text-danger">*</span></label>
        <input id="signup-country-input" class="form-control" type="text" v-model="model.country"
               placeholder="e.g., United States" required :readonly="!isManualAddress">
        <div id="signup-country-error" class="invalid-feedback d-none">Please enter your country.</div>
      </div>

      <div class="col-md-6">
        <label class="form-label f-w-600" for="signup-postal-code-input">Postal Code <span class="text-danger">*</span></label>
        <input id="signup-postal-code-input" class="form-control" type="text" v-model="model.postalCode"
               placeholder="e.g., 94102" required :readonly="!isManualAddress">
        <div id="signup-postal-code-error" class="invalid-feedback d-none">Please enter your postal code.</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
</style>
